import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import useAdminLoad from "../../../hooks/useAdminLoad";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import CrmCard from "../ui/CrmCard";
import CrmSwitch from "../ui/CrmSwitch";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import BirthdayMessageDialog from "./BirthdayMessageDialog";
import { CRM } from "../../../constants/testIds/crm";

/**
 * "Birthday wishes" (spec Screen 6): a single toggle plus "Edit message" — nothing else
 * to configure. Off by default. The toggle round-trips the CURRENT copy unedited; only
 * the dialog's Save changes subject/body.
 */
export default function BirthdayCard() {
  const t = useT("crm");
  const tc = useT("common");
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => { setSettings(await crmApi.getBirthdaySettings()); }, []);
  const { loadError, reload } = useAdminLoad(load);

  const save = async (patch) => {
    setBusy(true);
    try {
      const saved = await crmApi.updateBirthdaySettings({ ...settings, ...patch });
      setSettings(saved && typeof saved === "object" ? saved : { ...settings, ...patch });
      return true;
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (nextEnabled) => {
    const ok = await save({ enabled: nextEnabled });
    if (ok) toast.success(t(nextEnabled ? "email.birthdayOn" : "email.birthdayOff"));
  };

  const saveMessage = async (subject, bodyHtml) => {
    const ok = await save({ subject, body_html: bodyHtml });
    if (ok) { toast.success(t("email.birthdaySaved")); setEditing(false); }
  };

  if (loadError) return <CrmCard><CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.emailError} /></CrmCard>;
  if (!settings) return <CrmCard className="animate-pulse h-24" />;

  return (
    <CrmCard className="flex items-start justify-between gap-4">
      <div>
        <p className="font-display text-xl text-eh-ink">{t("email.birthdayTitle")}</p>
        <p className="mt-1 text-sm text-eh-muted max-w-md">{t("email.birthdayBody")}</p>
        <CrmButton variant="link" size="sm" className="mt-2 px-0" onClick={() => setEditing(true)} data-testid={CRM.emailBirthdayEdit}>
          {t("email.birthdayEdit")}
        </CrmButton>
      </div>
      <CrmSwitch checked={!!settings.enabled} onCheckedChange={toggle} disabled={busy} data-testid={CRM.emailBirthdayToggle} aria-label={t("email.birthdayTitle")} />
      <BirthdayMessageDialog open={editing} onOpenChange={setEditing} subject={settings.subject} bodyHtml={settings.body_html} onSave={saveMessage} busy={busy} />
    </CrmCard>
  );
}
